import { Input_Base } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Base.ts';
import { Html, html, View } from 'rune-ts';
import { z } from 'zod';
import { InputEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/type.ts';
import { spoTextOptionSchema } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/spo.ts';
import klass from './Input.module.scss';
import base_klass from '../../../../../shared/component/atom/Input/core/input.module.scss';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { ValueOf } from '../../../../../../../shared/type/util.type.ts';
import { PureInput, pureInputVariant } from '../../../../../../../shared/rune-component/Input/PureInput.ts';
import { StudioButton } from '@services/MarppleShop/shared/components/atoms/StudioButton/StudioButton.ts';
import { SPO_TEXT_OPTION__MAX } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { unEscaper } from '@shared/utils/escape.ts';

const DELETE_LABEL = '삭제';
const ADD_LABEL = '추가';

type SpoTextOption = z.infer<typeof spoTextOptionSchema>;

interface SpoTextOptionsData extends InputEditor {
  value: SpoTextOption[];
  setField: (keyName: ValueOf<typeof formKeyMap>, value: SpoTextOption[]) => void;
}

export class Input_SpoTextOptions extends Input_Base<SpoTextOptionsData> {
  private readonly MAX_COUNT = 3;
  private spo_text_options = this.data.value.length
    ? this.data.value.slice(0, this.MAX_COUNT)
    : [{ name: '' }];

  private optionHtmls = this.getTextOptionHtmls();

  protected override inputTemplate(): Html | View {
    return html`<div class="${klass.spo_text_options}">${this.optionHtmls}</div>`;
  }

  override onRender() {
    this.delegate('click', StudioButton, (_, view) => {
      switch (view.getLabel()) {
        case DELETE_LABEL: {
          this.deleteInput(view.getIndex());
          break;
        }
        case ADD_LABEL: {
          this.addInput();
          break;
        }
      }
    });
  }

  private getTextOptionHtmls() {
    return this.spo_text_options.length
      ? this.spo_text_options.map((option, index) =>
          this.makeTextOptionHtml({
            index,
            deleteButton: this.getDeleteButton(
              // 항목이 1개뿐인 첫 번째 옵션이면서 비어있는 경우에만 삭제 버튼 비활성화
              index === 0 && this.spo_text_options.length === 1 && this.isOptionEmpty(option),
              index,
            ),
            addButton: this.isShowAddButton(index)
              ? this.getAddButton(
                  // 해당 옵션이 완료되지 않은 경우 추가 버튼 비활성화
                  !this.isOptionCompleted(option),
                  index,
                )
              : undefined,
            value: unEscaper(option.name),
          }),
        )
      : [
          this.makeTextOptionHtml({
            index: 0,
            deleteButton: this.getDeleteButton(true, 0),
            addButton: this.getAddButton(true, 0),
            value: '',
          }),
        ];
  }

  private makeTextOptionHtml({
    index,
    deleteButton,
    addButton,
    value,
  }: {
    index: number;
    deleteButton: StudioButton;
    addButton?: StudioButton;
    value: string;
  }) {
    return html`
      <div class="${base_klass.input_container}">
        <div class="${klass.input_wrap}">
          ${new PureInput({
            variant: pureInputVariant.studio_editor,
            name: formKeyMap.spo_items,
            value,
            index,
            onInput: (view) => {
              this.setSpoTextOptionsByIndex(view.getValue(), view.getIndex());
              this.setButtonDisabled(view.getValue(), view.getIndex());
            },
            onFocusout: () => {
              this.setSpoTextOptionsState();
            },
            placeholder: '예시) 이니셜 각인을 입력해주세요.',
            maxlength: SPO_TEXT_OPTION__MAX,
          })}
          ${deleteButton} ${addButton ?? ''}
        </div>
      </div>
    `;
  }

  private setSpoTextOptionsByIndex(value: string, index: number) {
    this.spo_text_options = this.spo_text_options.map((text_option, idx) =>
      idx === index ? { ...text_option, name: value } : text_option,
    );

    return this;
  }

  private setButtonDisabled(value: string, index: number) {
    this.subViews(StudioButton)
      ?.filter((view) => view.getIndex() === index)
      .forEach((view) => {
        if (view.getLabel() === ADD_LABEL) {
          // 추가 버튼: 값이 비어있으면 비활성화
          view.setDisable(!value.trim());
        } else if (view.getLabel() === DELETE_LABEL) {
          // 삭제 버튼: 항목이 1개뿐인 첫 번째 옵션이면서 비어있는 경우에만 비활성화
          view.setDisable(index === 0 && this.spo_text_options.length === 1 && !value.trim());
        }
      });
  }

  private addInput() {
    if (this.spo_text_options.length >= this.MAX_COUNT) {
      return;
    }

    // 새로운 빈 옵션 추가
    this.spo_text_options.push({ name: '' });

    // HTML 재생성 및 재렌더링
    this.optionHtmls = this.getTextOptionHtmls();
    this.redraw();
  }

  private deleteInput(index: number) {
    // 항목이 1개뿐이고 첫 번째를 삭제하는 경우에만 초기화
    if (index === 0 && this.spo_text_options.length === 1) {
      // 첫 번째 옵션은 초기화만
      this.spo_text_options[0] = { name: '' };

      // 첫 번째 input의 값도 초기화
      const firstInput = this.subViews(PureInput)?.find((input) => input.getIndex() === 0);
      if (firstInput) {
        firstInput.setValue('');
      }

      // 삭제 버튼 비활성화
      const deleteButton = this.subViews(StudioButton)?.find(
        (button) => button.getIndex() === 0 && button.getLabel() === DELETE_LABEL,
      );
      if (deleteButton) {
        deleteButton.setDisable(true);
      }
    } else {
      // 해당 인덱스의 옵션 제거
      this.spo_text_options.splice(index, 1);

      this.optionHtmls = this.getTextOptionHtmls();
      this.redraw();
    }

    this.setSpoTextOptionsState();
  }

  private setSpoTextOptionsState() {
    this.data.setField(formKeyMap.spo_text_options, this.spo_text_options);
  }

  private isOptionEmpty(option: SpoTextOption) {
    return !option.name || option.name.trim() === '';
  }

  private isOptionCompleted(option: SpoTextOption) {
    return option.name && option.name.trim() !== '';
  }

  private isShowAddButton(index: number) {
    return index === this.spo_text_options.length - 1 && this.spo_text_options.length < this.MAX_COUNT;
  }

  private getDeleteButton(is_disabled: boolean, index: number) {
    return new StudioButton({
      custom_klass: klass.button,
      label: DELETE_LABEL,
      color: 'line_gray',
      size: 'free',
      is_disabled,
      index,
    });
  }

  private getAddButton(is_disabled: boolean, index: number) {
    return new StudioButton({
      custom_klass: klass.button,
      label: ADD_LABEL,
      color: 'line_gray',
      size: 'free',
      is_disabled,
      index,
    });
  }

  getName() {
    return this.data.name;
  }

  getValue() {
    return void 0;
  }

  setError() {
    return void 0;
  }
}
